import React, { useState, useEffect, useId } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavBar from '../pages/NavBar/NavBar';
import Button from '../common/Button';
import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css';
import { toast } from 'react-toastify';
import { getIdMeBaseApiUrl } from "../../utils/UrlConstants";
import FooterData from "../pages/NavBar/FooterData";
import { checkUserAuthentication, handleUnauthenticatedUser } from "../../utils/authUtils";
import { base64Utils } from "../../utils/base64Utils";
import { useApplications } from "../../hooks/useApplications";
import { displayPhoneNumber } from "../../utils/phoneUtils";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputText from '../common/InputText';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useIdentityAppService } from '../../services/IdentityServices';

interface UserDto {
    userId: number | string;
    userLogin: string;
    userType: string;
    userEmail: string;
    userPhone: string;
    userIdpGuid?: string;
    firstName: string;
    lastName: string;
    createdDate: string;
    modifiedDate: string;
    notes?: string;
    userStatus: string;
    displayName?: string;
}

interface UserAppRequestDto {
    id: number;
    userId: number;
    userLogin: string;
    firstName: string;
    lastName: string;
    userEmail: string;
    applicationCode: string;
    applicationName: string;
    roleCode: string;
    roleName: string;
    assignedTo: string;
    status: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    createdBy: string;
    modifiedBy: string;
    notes: string;
}

interface UserGroupsResponse {
    success: boolean;
    groups: UserGroup[];
    message: string;
    timestamp: string;
}

interface UserGroup {
    groupId: string;
    groupName: string;
    description: string;
    groupType: string;
    applicationCode: string;
    applicationName: string;
    assignedDate: string;
    requestCreatedDate?: string;
    startDate?: string;
    endDate?: string;
}

const InternalUserDetails: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { dynamicApplications } = useApplications();

    const user: UserDto = location.state?.user || null;
    const userTypeSelection: 'Internal' | 'External' = location.state?.userTypeSelection || 'Internal';
    const searchState = location.state?.searchState;

    // State management
    const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
    const [userRequests, setUserRequests] = useState<UserAppRequestDto[]>([]);
    const [filteredGroups, setFilteredGroups] = useState<UserGroup[]>([]);
    const [filteredRequests, setFilteredRequests] = useState<UserAppRequestDto[]>([]);
    const [groupsLoading, setGroupsLoading] = useState(true);
    const [requestsLoading, setRequestsLoading] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Revoke dialog state
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null);
    const [revokeOption, setRevokeOption] = useState<'immediate' | 'date'>('immediate');
    const [revokeDate, setRevokeDate] = useState<string>(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [revokeNotes, setRevokeNotes] = useState<string>('');
    const [revokingGroup, setRevokingGroup] = useState(false);

    // Request details dialog state
    const [requestDetailsDialogOpen, setRequestDetailsDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<UserAppRequestDto | null>(null);
    const [requestLifecycle, setRequestLifecycle] = useState<UserAppRequestDto[]>([]);
    const [requestLifecycleLoading, setRequestLifecycleLoading] = useState(false);

    // Load user's assigned groups and requests
    useEffect(() => {
        if (user) {
            loadUserGroups();
            loadUserRequests();
        }
    }, [user]);

    // Filter groups and requests based on admin's applications
    // COMMENTED OUT: Removing admin application filtering to show all groups/requests
    useEffect(() => {
        // if (dynamicApplications && dynamicApplications.length > 0) {
        //     const adminAppNames = dynamicApplications.map(app => app.applicationName.toLowerCase());
        //
        //     const filtered = userGroups.filter(group => {
        //         return adminAppNames.includes(group.applicationName?.toLowerCase());
        //     });
        //     setFilteredGroups(filtered);
        //
        //     const filteredReqs = userRequests.filter(request => {
        //         return adminAppNames.includes(request.applicationName?.toLowerCase());
        //     });
        //     setFilteredRequests(filteredReqs);
        // } else {
        //     setFilteredGroups(userGroups);
        //     setFilteredRequests(userRequests);
        // }

        // Show all groups and requests without filtering
        setFilteredGroups(userGroups);
        setFilteredRequests(userRequests);
    }, [dynamicApplications, userGroups, userRequests]);

    // Redirect if no user data
    React.useEffect(() => {
        if (!checkUserAuthentication()) {
            handleUnauthenticatedUser();
            return;
        }

        if (!user) {
            setErrors(prev => ({
                ...prev,
                general: 'No user data found. Redirecting to Internal User Management.'
            }));
            navigate('/admin-dashboard', { state: { userTypeSelection, searchState } });
        }
    }, [user, navigate, userTypeSelection, searchState]);

    const loadUserGroups = async () => {
        try {
            setGroupsLoading(true);
            const userId = user.userId;
            const url = `${getIdMeBaseApiUrl()}/user/internal/${userId}/groups`;

            const response = await useIdentityAppService().fetchUserGroupsInternal(userId);

            if (response.ok) {
                const data: UserGroupsResponse = await response.json();
                setUserGroups(data.groups || []);
            } else {
                console.error('Failed to load user groups');
                setUserGroups([]);
            }
        } catch (error) {
            console.error('Error loading user groups:', error);
            setUserGroups([]);
        } finally {
            setGroupsLoading(false);
        }
    };

    const loadUserRequests = async () => {
        try {
            setRequestsLoading(true);
            const userId = user.userId;
            const url = `${getIdMeBaseApiUrl()}/user/internal/${userId}/requests`;

            const response = await useIdentityAppService().fetchUserRequestsInternal(userId);

            if (response.ok) {
                const data = await response.json();
                setUserRequests(data.requests || []);
            } else {
                console.error('Failed to load user requests');
                setUserRequests([]);
            }
        } catch (error) {
            console.error('Error loading user requests:', error);
            setUserRequests([]);
        } finally {
            setRequestsLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/admin-dashboard', {
            state: {
                userTypeSelection: userTypeSelection,
                searchState
            }
        });
    };

    const handleViewRequestDetails = async (request: UserAppRequestDto) => {
        setSelectedRequest(request);
        setRequestDetailsDialogOpen(true);
        setRequestLifecycleLoading(true);

        try {
            const response = await useIdentityAppService().fetchRequestLifecycle(request.id);

            if (response.ok) {
                const lifecycleData: UserAppRequestDto[] = await response.json();
                setRequestLifecycle(lifecycleData);
            } else {
                console.error('Failed to fetch request lifecycle:', response.statusText);
                setRequestLifecycle([]);
            }
        } catch (error) {
            console.error('Error fetching request lifecycle:', error);
            setRequestLifecycle([]);
        } finally {
            setRequestLifecycleLoading(false);
        }
    };

    const handleCloseRequestDetailsDialog = () => {
        setRequestDetailsDialogOpen(false);
        setSelectedRequest(null);
        setRequestLifecycle([]);
    };

    // Handle opening the revoke dialog
    const handleRevokeClick = (group: UserGroup) => {
        setSelectedGroup(group);
        setRevokeDialogOpen(true);
        setRevokeOption('immediate');
        const today = new Date();
        setRevokeDate(today.toISOString().split('T')[0]);
        setRevokeNotes('');
    };

    // Handle closing the revoke dialog
    const handleCloseRevokeDialog = () => {
        setRevokeDialogOpen(false);
        setSelectedGroup(null);
        setRevokeOption('immediate');
        const today = new Date();
        setRevokeDate(today.toISOString().split('T')[0]);
        setRevokeNotes('');
    };

    // Handle revoke option change
    const handleRevokeOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as 'immediate' | 'date';
        setRevokeOption(value);
    };

    // Handle ReactQuill notes change
    const handleRevokeNotesChange = (content: string) => {
        setRevokeNotes(content);
    };

    // Check if submit button should be enabled
    const isRevokeSubmitEnabled = () => {
        const strippedNotes = revokeNotes.replace(/<[^>]*>/g, '').trim();
        if (!strippedNotes) {
            return false;
        }
        if (revokeOption === 'date' && !revokeDate) {
            return false;
        }
        return true;
    };

    // Handle revoke date change
    const handleRevokeDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRevokeDate(event.target.value);
    };

    // Handle revoke submit
    const handleRevokeSubmit = async () => {
        if (!selectedGroup || !isRevokeSubmitEnabled()) {
            return;
        }

        try {
            setRevokingGroup(true);

            const url = `${getIdMeBaseApiUrl()}/user/internal/group/revoke`;

            const requestBody = {
                userId: user.userId,
                groupId: selectedGroup.groupId,
                applicationCode: selectedGroup.applicationCode,
                revokeType: revokeOption,
                revokeDate: revokeOption === 'date' ? revokeDate : null,
                notes: base64Utils.encode(revokeNotes)
            };

            const response = await useIdentityAppService().postRevokeUserGroupInternal(JSON.stringify(requestBody));

            const responseData = await response.json();

            if (response.ok && responseData.success) {
                toast.success(`Group access ${revokeOption === 'immediate' ? 'revoked immediately' : 'scheduled for revocation'} successfully!`);
                handleCloseRevokeDialog();
                loadUserGroups();
                loadUserRequests();
            } else {
                toast.error(responseData.message || 'Failed to revoke group access');
            }
        } catch (error) {
            console.error('Error revoking group access:', error);
            toast.error('Network error occurred while revoking group access');
        } finally {
            setRevokingGroup(false);
        }
    };

    // Function to get status styles
    const getStatusStyles = (status: string) => {
        switch (status?.toUpperCase()) {
            case 'ACTIVE':
                return {
                    backgroundColor: '#E8F5E8',
                    color: '#4CAF50'
                };
            case 'PENDING':
            case 'INVITED':
                return {
                    backgroundColor: '#fff3cd',
                    color: '#856404'
                };
            case 'APPROVED':
                return {
                    backgroundColor: '#E8F5E8',
                    color: '#4CAF50'
                };
            case 'REJECTED':
            case 'ERROR':
            case 'REVOKED':
                return {
                    backgroundColor: '#FFEBEE',
                    color: '#f44336'
                };
            case 'SUSPENDED':
            case 'DISABLED':
                return {
                    backgroundColor: '#FFF3E0',
                    color: '#FF9800'
                };
            case 'LOCKED':
                return {
                    backgroundColor: '#FFEBEE',
                    color: '#f44336'
                };
            case 'INACTIVE':
                return {
                    backgroundColor: '#F5F5F5',
                    color: '#9E9E9E'
                };
            default:
                return {
                    backgroundColor: '#f5f5f5',
                    color: '#666'
                };
        }
    };

    // Grid columns for user groups (without Revoke action)
    const groupGridColumns = [
        {
            name: 'groupName',
            header: 'Group Name',
            defaultFlex: 2,
            minWidth: 150,
        },
        {
            name: 'applicationName',
            header: 'Application',
            defaultFlex: 1,
            minWidth: 100,
        },
        {
            name: 'description',
            header: 'Description',
            defaultFlex: 2,
            minWidth: 100,
        },
        {
            name: 'assignedDate',
            header: 'Assigned Date',
            defaultWidth: 130,
            render: ({ value }: { value: string }) => {
                return value ? new Date(value).toLocaleDateString() : '';
            }
        },
        {
            name: 'requestCreatedDate',
            header: 'Request Created Date',
            defaultWidth: 180,
            render: ({ value }: { value: string }) => {
                return value ? new Date(value).toLocaleDateString() : '';
            }
        },
        {
            name: 'startDate',
            header: 'Start Date',
            defaultFlex: 1,
            minWidth: 100,
            render: ({ value }: { value: string | number[] }) => {
                if (!value) return '';
                // Handle both array format [2026, 1, 15] and string format "2026-01-15"
                if (Array.isArray(value)) {
                    const [year, month, day] = value;
                    return new Date(year, month - 1, day).toLocaleDateString();
                }
                return new Date(value).toLocaleDateString();
            }
        },
        {
            name: 'endDate',
            header: 'End Date',
            defaultFlex: 1,
            minWidth: 100,
            render: ({ value }: { value: string | number[] }) => {
                if (!value) return '';
                // Handle both array format [2026, 1, 15] and string format "2026-01-15"
                if (Array.isArray(value)) {
                    const [year, month, day] = value;
                    return new Date(year, month - 1, day).toLocaleDateString();
                }
                return new Date(value).toLocaleDateString();
            }
        },
        {
            name: 'groupType',
            header: 'Type',
            defaultWidth: 100,
        },
        {
            name: 'action',
            header: 'Action',
            defaultWidth: 120,
            render: ({ data }: { data: UserGroup }) => {
                const isEndDateInPast = data.endDate ? new Date(data.endDate) < new Date() : false;
                return (
                    <span title={isEndDateInPast ? 'Cannot revoke - end date has already passed' : ''}>
                        <Button
                            onClick={() => handleRevokeClick(data)}
                            variation="outline"
                            disabled={isEndDateInPast}
                        >
                            Revoke
                        </Button>
                    </span>
                );
            }
        }
    ];

    // Grid columns for user requests
    const requestGridColumns = [
        {
            name: 'id',
            header: 'Request ID',
            defaultWidth: 100,
            type: 'number',
        },
        {
            name: 'applicationName',
            header: 'Application',
            defaultFlex: 1,
            minWidth: 150,
        },
        {
            name: 'roleName',
            header: 'Role',
            defaultFlex: 1,
            minWidth: 150,
        },
        {
            name: 'status',
            header: 'Status',
            defaultWidth: 120,
            render: ({ value }: { value: string }) => {
                const statusStyles = getStatusStyles(value);

                return (
                    <span style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        backgroundColor: statusStyles.backgroundColor,
                        color: statusStyles.color,
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }}>
                        {value}
                    </span>
                );
            }
        },
        {
            name: 'assignedTo',
            header: 'Assigned To',
            defaultFlex: 1,
            minWidth: 120,
        },
        {
            name: 'createdDate',
            header: 'Created Date',
            defaultWidth: 130,
            render: ({ value }: { value: string }) => {
                return value ? new Date(value).toLocaleDateString() : '';
            }
        },
        {
            name: 'action',
            header: 'Action',
            defaultWidth: 100,
            render: ({ data }: { data: UserAppRequestDto }) => {
                return (
                    <Button
                        onClick={() => handleViewRequestDetails(data)}
                        variation="outline"
                    >
                        Details
                    </Button>
                );
            }
        }
    ];

    if (!user) {
        return <div>Loading...</div>;
    }

    const statusStyles = getStatusStyles(user?.userStatus);

    return (
        <React.Fragment>
            <NavBar />

            <div style={{ marginLeft: '20px', marginRight: '20px', minHeight: '300px' }}>
                <div className="subheading grid-row">
                    <div className="grid-col flex-1">
                        <h3 className="subheading-title" style={{
                            textAlign: 'center',
                            marginBottom: '20px',
                            marginTop: '40px'
                        }}>
                            Internal User Details - {user.firstName} {user.lastName}
                        </h3>
                    </div>
                </div>

                <div style={{
                    padding: '30px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    margin: '0 auto',
                    maxWidth: '1200px',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                }}>
                    {/* User Details Section */}
                    <div style={{ marginBottom: '40px' }}>
                        <h4 style={{
                            margin: '0 0 20px 0',
                            color: '#00607f',
                            borderBottom: '2px solid #00607f',
                            paddingBottom: '8px'
                        }}>
                            User Details
                        </h4>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '30px',
                            marginBottom: '20px'
                        }}>
                            <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                                <h5 style={{ margin: '0 0 15px 0', color: '#00607f' }}>Personal Information</h5>
                                <p style={{ margin: '8px 0' }}><strong>User ID:</strong> {user.userId}</p>
                                <p style={{ margin: '8px 0' }}><strong>Display Name:</strong> {user.displayName || user.userLogin}</p>
                                <p style={{ margin: '8px 0' }}><strong>First Name:</strong> {user.firstName}</p>
                                <p style={{ margin: '8px 0' }}><strong>Last Name:</strong> {user.lastName}</p>
                                <p style={{ margin: '8px 0' }}><strong>Email:</strong> {user.userEmail}</p>
                                <p style={{ margin: '8px 0' }}>
                                    <strong>Phone:</strong> {displayPhoneNumber(user.userPhone) || 'Not provided'}
                                </p>
                                <p style={{ margin: '8px 0' }}><strong>User Type:</strong> {user.userType}</p>
                            </div>
                            <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                                <h5 style={{ margin: '0 0 15px 0', color: '#00607f' }}>Account Information</h5>
                                <p style={{ margin: '8px 0' }}>
                                    <strong>Status:</strong>
                                    <span style={{
                                        padding: '4px 10px',
                                        borderRadius: '4px',
                                        backgroundColor: statusStyles.backgroundColor,
                                        color: statusStyles.color,
                                        marginLeft: '8px',
                                        fontSize: '14px',
                                        fontWeight: 'bold'
                                    }}>
                                        {user.userStatus || ''}
                                    </span>
                                </p>

                                <p style={{ margin: '8px 0' }}><strong>IDP GUID:</strong> {user.userIdpGuid || ''}</p>
                                <p style={{ margin: '8px 0' }}>
                                    <strong>Created Date:</strong> {user.createdDate && !Number.isNaN(new Date(user.createdDate).getTime()) ? new Date(user.createdDate).toLocaleString() : ''}
                                </p>
                                <p style={{ margin: '8px 0' }}>
                                    <strong>Modified Date:</strong> {user.modifiedDate && !Number.isNaN(new Date(user.modifiedDate).getTime()) ? new Date(user.modifiedDate).toLocaleString() : ''}
                                </p>
                            </div>
                        </div>

                        {/* General Error Display */}
                        {errors.general && (
                            <div style={{ color: "red", marginTop: "10px", marginBottom: "10px", textAlign: "center" }}>
                                {errors.general}
                            </div>
                        )}
                    </div>

                    {/* Assigned Groups Section */}
                    <div style={{ marginBottom: '40px' }}>
                        <h4 style={{
                            margin: '0 0 20px 0',
                            color: '#00607f',
                            borderBottom: '2px solid #00607f',
                            paddingBottom: '8px'
                        }}>
                            Assigned Roles ({filteredGroups.length})
                            {dynamicApplications && dynamicApplications.length > 0 && (
                                <span
                                    style={{ fontSize: '14px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                                    (Showing only for your applications)
                                </span>
                            )}
                        </h4>

                        {groupsLoading ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                Loading assigned groups...
                            </div>
                        ) : filteredGroups.length > 0 ? (
                            <ReactDataGrid
                                licenseKey="AppName=CoherenceApp,Company=Coherence,ExpiryDate=2025-04-15T00:00:00.000Z,LicenseDeveloperCount=1,LicenseType=single_app,Ref=CoherenceLicenseRef,Z=983320388-1513273079-1127323721-2081795733-199375339958733905"
                                idProperty="groupId"
                                pagination
                                columns={groupGridColumns}
                                dataSource={filteredGroups}
                                sortable={true}
                                defaultLimit={10}
                                rowHeight={50}
                                enableColumnAutosize={true}
                                style={{ minHeight: 300 }}
                            />
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px',
                                color: '#666',
                                fontSize: '16px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px'
                            }}>
                                <h5 style={{ marginBottom: '10px' }}>No Groups Assigned</h5>
                                <p>This user is not currently assigned to any groups for your applications.</p>
                            </div>
                        )}
                    </div>

                    {/* Role Requests Section */}
                    <div style={{ marginBottom: '30px' }}>
                        <h4 style={{
                            margin: '0 0 20px 0',
                            color: '#00607f',
                            borderBottom: '2px solid #00607f',
                            paddingBottom: '8px'
                        }}>
                            Role Requests ({filteredRequests.length})
                            {dynamicApplications && dynamicApplications.length > 0 && (
                                <span
                                    style={{ fontSize: '14px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                                    (Showing only for your applications)
                                </span>
                            )}
                        </h4>

                        {requestsLoading ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                Loading role requests...
                            </div>
                        ) : filteredRequests.length > 0 ? (
                            <ReactDataGrid
                                licenseKey="AppName=CoherenceApp,Company=Coherence,ExpiryDate=2025-04-15T00:00:00.000Z,LicenseDeveloperCount=1,LicenseType=single_app,Ref=CoherenceLicenseRef,Z=983320388-1513273079-1127323721-2081795733-199375339958733905"
                                idProperty="id"
                                pagination
                                columns={requestGridColumns}
                                dataSource={filteredRequests}
                                sortable={true}
                                defaultLimit={10}
                                rowHeight={50}
                                enableColumnAutosize={true}
                                style={{ minHeight: 300 }}
                                defaultSortInfo={[{ name: 'createdDate', dir: -1 }]}
                            />
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px',
                                color: '#666',
                                fontSize: '16px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px'
                            }}>
                                <h5 style={{ marginBottom: '10px' }}>No Role Requests</h5>
                                <p>This user has no role assignment requests for your applications.</p>
                            </div>
                        )}
                    </div>

                    {/* Back Button */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: '20px',
                        borderTop: '1px solid #eee'
                    }}>
                        <Button onClick={handleBack}>Back to Admin Dashboard</Button>
                    </div>
                </div>
            </div>

            {/* Revoke Dialog */}
            <Dialog
                open={revokeDialogOpen}
                onClose={handleCloseRevokeDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle style={{ backgroundColor: '#00607f', color: 'white', padding: '16px 24px' }}>
                    Revoke Group Access
                </DialogTitle>
                <DialogContent style={{ padding: '24px' }}>
                    {selectedGroup && (
                        <div style={{ marginBottom: '20px' }}>
                            <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                <strong>Group:</strong> {selectedGroup.groupName}
                            </p>
                            <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                <strong>Application:</strong> {selectedGroup.applicationName}
                            </p>
                        </div>
                    )}

                    <FormControl component="fieldset" style={{ marginBottom: '20px', width: '100%' }}>
                        <RadioGroup
                            value={revokeOption}
                            onChange={handleRevokeOptionChange}
                        >
                            <FormControlLabel
                                value="immediate"
                                control={<Radio style={{ color: '#00607f' }} />}
                                label="Revoke Immediately"
                                style={{ marginBottom: '10px' }}
                            />
                            <FormControlLabel
                                value="date"
                                control={<Radio style={{ color: '#00607f' }} />}
                                label="Schedule Revocation"
                            />
                        </RadioGroup>
                    </FormControl>

                    {revokeOption === 'date' && (
                        <div style={{ marginBottom: '20px' }}>
                            <InputText
                                label="Revocation Date"
                                name="revokeDate"
                                value={revokeDate}
                                type="date"
                                onChange={handleRevokeDateChange}
                                mandatoryLabel={true}
                                readOnly={revokingGroup}
                            />
                        </div>
                    )}

                    <div style={{ marginBottom: '10px' }}>
                        <label htmlFor="revoke-notes"
                            style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                            <b>Notes:</b> <span style={{ color: 'red' }}>*</span>
                        </label>
                        <ReactQuill
                            id="revoke-notes"
                            value={revokeNotes}
                            onChange={handleRevokeNotesChange}
                            style={{ maxHeight: '150px', overflowY: 'auto' }}
                            placeholder="Please provide a reason for revoking access (required)"
                            modules={{
                                toolbar: [
                                    ['bold', 'italic', 'underline', 'strike'],
                                    [{ list: 'ordered' }, { list: 'bullet' }],
                                    [{ align: [] }],
                                    ['clean'],
                                ],
                            }}
                        />
                    </div>
                </DialogContent>
                <DialogActions style={{ padding: '16px 24px' }}>
                    <Button
                        onClick={handleCloseRevokeDialog}
                        variation="outline"
                        disabled={revokingGroup}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRevokeSubmit}
                        disabled={!isRevokeSubmitEnabled() || revokingGroup}
                    >
                        {revokingGroup ? 'Revoking...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Request Details Dialog */}
            <Dialog
                open={requestDetailsDialogOpen}
                onClose={handleCloseRequestDetailsDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle style={{ backgroundColor: '#00607f', color: 'white', padding: '16px 24px' }}>
                    Request Details
                </DialogTitle>
                <DialogContent style={{ padding: '24px' }}>
                    {selectedRequest && (
                        <>
                            {/* User Information */}
                            <div style={{ marginBottom: '20px' }}>
                                <h5 style={{ margin: '0 0 15px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px' }}>
                                    User Information
                                </h5>
                                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '6px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                        <p style={{ margin: '5px 0' }}><strong>Username:</strong> {selectedRequest.userLogin}</p>
                                        <p style={{ margin: '5px 0' }}><strong>Name:</strong> {selectedRequest.firstName} {selectedRequest.lastName}</p>
                                        <p style={{ margin: '5px 0' }}><strong>Email:</strong> {selectedRequest.userEmail}</p>
                                        <p style={{ margin: '5px 0' }}><strong>User ID:</strong> {selectedRequest.userId}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Request Information */}
                            <div style={{ marginBottom: '20px' }}>
                                <h5 style={{ margin: '0 0 15px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px' }}>
                                    Request Information
                                </h5>
                                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '6px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                        <p style={{ margin: '5px 0' }}><strong>Request ID:</strong> {selectedRequest.id}</p>
                                        <p style={{ margin: '5px 0' }}>
                                            <strong>Status:</strong>
                                            <span style={{
                                                padding: '3px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: getStatusStyles(selectedRequest.status).backgroundColor,
                                                color: getStatusStyles(selectedRequest.status).color,
                                                marginLeft: '8px',
                                                fontSize: '12px',
                                                fontWeight: 'bold'
                                            }}>
                                                {selectedRequest.status}
                                            </span>
                                        </p>
                                        <p style={{ margin: '5px 0' }}><strong>Application:</strong> {selectedRequest.applicationName} ({selectedRequest.applicationCode})</p>
                                        <p style={{ margin: '5px 0' }}><strong>Role:</strong> {selectedRequest.roleName} ({selectedRequest.roleCode})</p>
                                        <p style={{ margin: '5px 0' }}><strong>Assigned To:</strong> {selectedRequest.assignedTo}</p>
                                        <p style={{ margin: '5px 0' }}><strong>Operational Flag:</strong> {selectedRequest.operationalFlag}</p>
                                        <p style={{ margin: '5px 0' }}><strong>Created:</strong> {new Date(selectedRequest.createdDate).toLocaleString()}</p>
                                        <p style={{ margin: '5px 0' }}><strong>Created By:</strong> {selectedRequest.createdBy || 'N/A'}</p>
                                        <p style={{ margin: '5px 0' }}><strong>Modified:</strong> {new Date(selectedRequest.modifiedDate).toLocaleString()}</p>
                                        <p style={{ margin: '5px 0' }}><strong>Modified By:</strong> {selectedRequest.modifiedBy || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Role & Application Details */}
                            <div style={{ marginBottom: '20px' }}>
                                <h5 style={{ margin: '0 0 15px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px' }}>
                                    Role & Application Details
                                </h5>
                                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '6px' }}>
                                    <p style={{ margin: '5px 0' }}><strong>Application:</strong> {selectedRequest.applicationName} ({selectedRequest.applicationCode})</p>
                                    <p style={{ margin: '5px 0' }}><strong>Role:</strong> {selectedRequest.roleName} ({selectedRequest.roleCode})</p>
                                </div>
                            </div>

                            {/* Notes */}
                            {selectedRequest.notes && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h5 style={{ margin: '0 0 15px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px' }}>
                                        Notes
                                    </h5>
                                    <div style={{
                                        padding: '15px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '6px',
                                        border: '1px solid #dee2e6'
                                    }}>
                                        {base64Utils.decode(selectedRequest.notes)}
                                    </div>
                                </div>
                            )}

                            {/* Request Lifecycle */}
                            <div>
                                <h5 style={{ margin: '0 0 15px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px' }}>
                                    Request History & Lifecycle
                                </h5>
                                {requestLifecycleLoading ? (
                                    <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                        Loading request history...
                                    </div>
                                ) : requestLifecycle.length > 0 ? (
                                    <div style={{
                                        backgroundColor: '#f8f9fa',
                                        padding: '20px',
                                        borderRadius: '6px',
                                        border: '1px solid #dee2e6',
                                        maxHeight: '300px',
                                        overflowY: 'auto'
                                    }}>
                                        {requestLifecycle.map((lifecycleItem, index) => {
                                            const isLast = index === requestLifecycle.length - 1;
                                            const itemStatusStyles = getStatusStyles(lifecycleItem.status);

                                            return (
                                                <div key={lifecycleItem.id} style={{
                                                    position: 'relative',
                                                    paddingLeft: '40px',
                                                    paddingBottom: isLast ? '0' : '25px'
                                                }}>
                                                    {/* Timeline line */}
                                                    {!isLast && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            left: '15px',
                                                            top: '25px',
                                                            bottom: '0',
                                                            width: '2px',
                                                            backgroundColor: '#dee2e6'
                                                        }} />
                                                    )}

                                                    {/* Timeline dot */}
                                                    <div style={{
                                                        position: 'absolute',
                                                        left: '8px',
                                                        top: '8px',
                                                        width: '16px',
                                                        height: '16px',
                                                        borderRadius: '50%',
                                                        backgroundColor: itemStatusStyles.color,
                                                        border: '3px solid white',
                                                        boxShadow: '0 0 0 2px ' + itemStatusStyles.color
                                                    }} />

                                                    {/* Timeline content */}
                                                    <div style={{
                                                        backgroundColor: 'white',
                                                        padding: '15px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #dee2e6',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'flex-start',
                                                            marginBottom: '10px'
                                                        }}>
                                                            <div>
                                                                <span style={{
                                                                    padding: '3px 8px',
                                                                    borderRadius: '4px',
                                                                    backgroundColor: itemStatusStyles.backgroundColor,
                                                                    color: itemStatusStyles.color,
                                                                    fontSize: '12px',
                                                                    fontWeight: 'bold',
                                                                    marginRight: '10px'
                                                                }}>
                                                                    {lifecycleItem.status}
                                                                </span>
                                                                <span style={{
                                                                    fontSize: '12px',
                                                                    color: '#666',
                                                                    fontWeight: 'bold'
                                                                }}>
                                                                    Request ID: {lifecycleItem.id}
                                                                </span>
                                                            </div>
                                                            <span style={{
                                                                fontSize: '12px',
                                                                color: '#666',
                                                                backgroundColor: lifecycleItem.operationalFlag === 'ACTIVE' ? '#e8f5e8' : '#f8f9fa',
                                                                padding: '2px 6px',
                                                                borderRadius: '3px',
                                                                border: '1px solid ' + (lifecycleItem.operationalFlag === 'ACTIVE' ? '#4CAF50' : '#dee2e6')
                                                            }}>
                                                                {lifecycleItem.operationalFlag}
                                                            </span>
                                                        </div>

                                                        <div style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: '1fr 1fr',
                                                            gap: '15px',
                                                            fontSize: '14px'
                                                        }}>
                                                            <div>
                                                                <p style={{ margin: '4px 0' }}>
                                                                    <strong>Created:</strong> {new Date(lifecycleItem.createdDate).toLocaleString()}
                                                                </p>
                                                                <p style={{ margin: '4px 0' }}>
                                                                    <strong>Created By:</strong> {lifecycleItem.createdBy || 'N/A'}
                                                                </p>
                                                                <p style={{ margin: '4px 0' }}>
                                                                    <strong>Modified:</strong> {new Date(lifecycleItem.modifiedDate).toLocaleString()}
                                                                </p>
                                                                <p style={{ margin: '4px 0' }}>
                                                                    <strong>Modified By:</strong> {lifecycleItem.modifiedBy || 'N/A'}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p style={{ margin: '4px 0' }}>
                                                                    <strong>Assigned To:</strong> {lifecycleItem.assignedTo}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {lifecycleItem.notes && (
                                                            <div style={{
                                                                marginTop: '10px',
                                                                padding: '8px',
                                                                backgroundColor: '#f8f9fa',
                                                                borderRadius: '4px',
                                                                fontSize: '13px'
                                                            }}>
                                                                <strong>Notes:</strong>
                                                                <div style={{ marginTop: '4px' }}>
                                                                    {base64Utils.decode(lifecycleItem.notes)}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '20px',
                                        color: '#666',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '6px'
                                    }}>
                                        No request history available
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '16px 24px' }}>
                    <Button onClick={handleCloseRequestDetailsDialog} variation="outline">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <FooterData />
        </React.Fragment>
    );
};

export default InternalUserDetails;
